/**
 * LeftShield Admin - Authentication Hook
 *
 * Custom hook providing authentication state and actions for the Admin App.
 */
import { useMsal, useAccount, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus, InteractionRequiredAuthError } from '@azure/msal-browser';
import { useCallback, useMemo } from 'react';
import { loginRequest, apiScopes } from '../config';

export interface AuthUser {
  id: string;
  displayName: string;
  email: string;
  roles: string[];
}

export interface UseAuthResult {
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Whether an authentication operation is in progress */
  isLoading: boolean;
  /** The authenticated user (null if not authenticated) */
  user: AuthUser | null;
  /** Sign in with popup */
  signIn: () => Promise<void>;
  /** Sign out */
  signOut: () => Promise<void>;
  /** Get access token for API calls */
  getAccessToken: () => Promise<string | null>;
  /** Error from last auth operation */
  error: Error | null;
}

/**
 * Hook for authentication state and actions
 */
export function useAuth(): UseAuthResult {
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || null);
  const isAuthenticated = useIsAuthenticated();

  const isLoading = inProgress !== InteractionStatus.None;

  // Map account to user object
  const user = useMemo<AuthUser | null>(() => {
    if (!account) return null;

    // Extract roles from ID token claims
    const roles = (account.idTokenClaims?.roles as string[]) || [];

    return {
      id: account.localAccountId || account.homeAccountId,
      displayName: account.name || 'Unknown User',
      email: account.username || '',
      roles,
    };
  }, [account]);

  // Sign in with popup
  const signIn = useCallback(async () => {
    try {
      await instance.loginPopup(loginRequest);
    } catch (error) {
      console.error('Sign in failed:', error);
      throw error;
    }
  }, [instance]);

  // Sign out
  const signOut = useCallback(async () => {
    try {
      await instance.logoutPopup({
        postLogoutRedirectUri: '/',
        mainWindowRedirectUri: '/',
      });
    } catch (error) {
      console.error('Sign out failed:', error);
      throw error;
    }
  }, [instance]);

  // Get access token for API calls
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!account) return null;

    try {
      const response = await instance.acquireTokenSilent({
        scopes: apiScopes.platform,
        account,
      });
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Token expired or consent required - use popup
        try {
          const response = await instance.acquireTokenPopup({
            scopes: apiScopes.platform,
          });
          return response.accessToken;
        } catch (popupError) {
          console.error('Token acquisition via popup failed:', popupError);
          return null;
        }
      }
      console.error('Token acquisition failed:', error);
      return null;
    }
  }, [instance, account]);

  return {
    isAuthenticated,
    isLoading,
    user,
    signIn,
    signOut,
    getAccessToken,
    error: null,
  };
}

export default useAuth;
